import {memo} from 'react';
import type {SxProps, Theme} from '@mui/material/styles';
import Image from 'next/image';
import {MUIBox, MUIContainer, MUIGrid, MUITypography} from '@/components/MUI-Components';
import {csx} from '../shared/caseStudyStyles';
import {sectionClass} from '../shared/sectionClass';
import SectionHeader from '../shared/SectionHeader';
import {colors} from '@/styles/tokens';
import type {FeatureGridSection} from '@/types/caseStudy';

// Table body row: highlight with the accent tint + an accent left rail on hover.
const rowHover: SxProps<Theme> = {
    transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
    boxShadow: 'inset 3px 0 0 transparent',
    '&:hover': {
        backgroundColor: 'var(--cs-accent-bg) !important',
        boxShadow: 'inset 3px 0 0 var(--cs-dark-bg)'
    }
};

// Step row: light accent tint on hover.
const stepHover: SxProps<Theme> = {
    borderRadius: '10px',
    paddingInline: '16px',
    transition: 'background-color 0.2s ease',
    '&:hover': {backgroundColor: `var(--cs-accent-bg, ${colors.background.subtle})`}
};

// Numbered vertical step list (left column of the 'steps' layout).
const StepList = ({cards}: {cards: FeatureGridSection['cards']}) => (
    <MUIBox>
        {cards.map((card, i) => (
            <MUIBox key={i} sx={[csx.stepRow, stepHover]}>
                <MUIBox sx={csx.stepNum}>{i + 1}</MUIBox>
                <MUIBox>
                    <MUITypography sx={csx.cardTitle}>{card.title}</MUITypography>
                    <MUITypography className="font-regular-text">{card.desc}</MUITypography>
                </MUIBox>
            </MUIBox>
        ))}
    </MUIBox>
);

/**
 * A titled block of title + description items. Two layouts:
 *  - 'grid'  (default): centered card grid, optional image below the header.
 *  - 'steps': numbered list on the left, illustration on the right.
 * Reused for "How It Works" (steps) and "Hospital Configuration" (grid).
 */
const FeatureGrid = ({eyebrow, heading, intro, image, layout = 'grid', columns, cards, spacing}: FeatureGridSection) => (
    <MUIBox className={sectionClass(spacing)}>
        <MUIContainer>
            <SectionHeader eyebrow={eyebrow} heading={heading} subheading={intro} wideHeading />

            {layout === 'table' ? (
                <MUIBox sx={csx.configTable}>
                    <MUIBox sx={[csx.configRow, csx.configHead]}>
                        <MUITypography sx={csx.configHeadCell}>{columns?.left ?? 'Area'}</MUITypography>
                        <MUITypography sx={csx.configHeadCell}>{columns?.right ?? 'Purpose'}</MUITypography>
                    </MUIBox>
                    {cards.map((card, i) => (
                        <MUIBox sx={[csx.configRow, rowHover]} key={i}>
                            <MUITypography sx={[csx.cardTitle, csx.configCell]}>{card.title}</MUITypography>
                            <MUITypography className="font-regular-text" sx={csx.configCell}>
                                {card.desc}
                            </MUITypography>
                        </MUIBox>
                    ))}
                </MUIBox>
            ) : layout === 'steps' ? (
                <MUIGrid container spacing={5} alignItems="center">
                    {/* With no illustration the steps would sit in a half-width column
                        beside blank space, so they take the full row instead. */}
                    <MUIGrid item xs={12} md={image ? 6 : 12}>
                        <StepList cards={cards} />
                    </MUIGrid>
                    {image ? (
                        <MUIGrid item xs={12} md={6}>
                            <MUIBox
                                sx={{
                                    width: '100%',
                                    p: '20px',
                                    mx: 'auto',
                                    border: `4px solid ${colors.primary.featureDot}`,
                                    borderRadius: '30px',
                                    overflow: 'hidden',
                                    boxShadow: 'var(--cs-shadow-md)',
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': {transform: 'scale(1.03)', boxShadow: '0 16px 40px rgba(16, 24, 40, 0.16)'}
                                }}
                            >
                                <Image
                                    src={image}
                                    alt={heading}
                                    width={944}
                                    height={1102}
                                    unoptimized
                                    loading="lazy"
                                    style={{width: '100%', height: 'auto', maxWidth: '100%', display: 'block'}}
                                />
                            </MUIBox>
                        </MUIGrid>
                    ) : null}
                </MUIGrid>
            ) : (
                <>
                    {image ? (
                        <MUIBox sx={{maxWidth: 900, mx: 'auto', mb: 4}}>
                            <Image
                                src={image}
                                alt={heading}
                                width={900}
                                height={520}
                                loading="lazy"
                                style={{width: '100%', height: 'auto', maxWidth: '100%'}}
                            />
                        </MUIBox>
                    ) : null}
                    <MUIGrid container spacing={3}>
                        {cards.map((card, i) => (
                            <MUIGrid item xs={12} sm={6} md={4} key={i}>
                                <MUIBox sx={csx.card}>
                                    <MUITypography sx={csx.cardTitle}>{card.title}</MUITypography>
                                    <MUITypography className="font-regular-text">{card.desc}</MUITypography>
                                </MUIBox>
                            </MUIGrid>
                        ))}
                    </MUIGrid>
                </>
            )}
        </MUIContainer>
    </MUIBox>
);

export default memo(FeatureGrid);
